/**
 * ChatScopeBar — the chat input's scope-indicator chip (Agents Phase 4, piece 3;
 * design §3). Renders a thin bar above the composer when the chat is scoped to a run
 * ("talking to ● {agent}"), and nothing when scoped to the orchestrator (the default).
 *
 * Lives in the chatbox/* cluster — the sanctioned cross-module-import locus (ChatModulePane
 * already imports `@/modules/*`) — so it can reuse the agents `ScopeChip` without tripping
 * the import-direction guard, and keeps the ChatBox composer diff to a single additive line.
 *
 * VISUAL scoping only: it reads `useChatAgentScope()` and the × calls `clearScope()`. No
 * message routing here (that is deferred/live work). The `.agui` wrapper scopes the agents
 * `--agui-*` design tokens so the reused chip resolves its colors outside the Agents module.
 */
import { useChatAgentScopeOptional } from '@/app/providers/ChatAgentScopeContext'
import { ScopeChip } from '@/modules/agents/redesign/scoped-chat'
import type { Agent, AgentStatus } from '@/modules/agents/redesign/fixtures'

import '@/modules/agents/redesign/agents-redesign.css'

export function ChatScopeBar() {
  // Optional read: the ChatBox composer can mount in isolated harnesses without the
  // provider; there it reads as the orchestrator (null scope) and renders nothing.
  const { scope, clearScope } = useChatAgentScopeOptional()
  if (!scope) return null

  // ScopeChip reads only name/color/status off the agent; the scope carries that minimal
  // identity. The cast narrows the structural subset to Agent (the chip never touches the rest).
  const status: AgentStatus = scope.agent.status === 'paused' ? 'paused' : 'active'
  const chipAgent = {
    id: scope.agentId,
    name: scope.agent.name,
    color: scope.agent.color,
    status,
  } as Pick<Agent, 'id' | 'name' | 'color' | 'status'> as Agent

  return (
    <div className="agui chat-agent-scope-bar">
      <ScopeChip agent={chipAgent} onClear={clearScope} />
    </div>
  )
}
